import {
  Grid,
  IconButton,
  TextField,
  Typography,
  Box,
  Paper,
  Dialog,
  AppBar,
  Toolbar,
  DialogContent as MuiDialogContent,
  Button
} from "@mui/material";
import { makeStyles } from '@mui/styles';
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import EditIcon from "@mui/icons-material/Edit";
import React, { useContext, useState } from "react";
import { ReservationContext } from "../../../contexts/reservation.context";
import { DashboardContext } from "../../../contexts/dashboard.context";
import { styleVariants, styleOptions } from "../../../style";
import ConfirmDialog from "../../customComponents/confirm.dialog";
import UserReservationDialog from "../reservation/user.reservation.dish.dialog";
import DishVariantsSelector from "../reservation/DishVariantsSelector";

const useStyles = makeStyles((theme) => ({
  noteSection: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  paperItem: {
    marginBottom: theme.spacing(2), // Spazio tra i piatti
    padding: theme.spacing(2),
  },
  variantDetails: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
}));

const UserCartDish = ({ dish, dishIndex, isOwner }) => {
  const classes = useStyles();
  const [amount, setAmount] = useState(dish.amount);
  const [note, setNote] = useState(dish.note || "");
  const [showNote, setShowNote] = useState(!!dish.note);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogText, setDialogText] = useState({ text: null, description: null });
  const [openVariantDialog, setOpenVariantDialog] = useState(false);
  const [dishForDialog, setDishForDialog] = useState(null);
  const { getDishPrice, removeFromCart, setDishNote, changeAmount, updateDishPrice, updateDishVariants, dishes } =
    useContext(ReservationContext);

  const { dataMenuObj } = useContext(DashboardContext) || {};
  const [currentDeletedIds, setCurrentDeletedIds] = useState([]);

  const handleChangeAmount = (value) => {
    setAmount(value);
    changeAmount(dishIndex, value);
  };

  const handleEditVariants = () => {
    // Trova il piatto originale
    const originalDish = isOwner
      ? dataMenuObj?.dataMenu?.find(d => d.id === dish.id)
      : dishes.find(d => d.id === dish.id);
    if (!originalDish) return;

    // Costruisci le varianti combinate con selected e deletedOptions
    const combinedVariants = originalDish.variants?.map(variant => {
      const selectedVariant = dish.variants?.find(v => v.id === variant.id);
      return {
        ...variant,
        selected: selectedVariant?.selected || [],
        deletedOptions: Array.isArray(selectedVariant?.deletedOptions)
          ? [...selectedVariant.deletedOptions]
          : [],
      };
    }) || [];

    // Ordina le varianti se sei owner
    const sortedVariants = isOwner
      ? combinedVariants.sort((a, b) => a.position - b.position)
      : combinedVariants;

    const dishForDialogBuilt = { ...originalDish, variants: sortedVariants };
    setDishForDialog(dishForDialogBuilt);

    if (isOwner) {
      // Inizializza gli stati per il dialog
      const initialDeletedOptions = new Set();
      dish.variants?.forEach(v =>
        v.deletedOptions?.forEach(optId => initialDeletedOptions.add(optId))
      );

      // Ricostruisci la nota "NO …" dalle deletedOptions
      const initialDeletedNotesString = Array.from(initialDeletedOptions)
        .map(optionId => {
          for (const variant of dishForDialogBuilt.variants) {
            const opt = variant.options.find(o => o.id === optionId);
            if (opt) return `NO ${opt.label}`;
          }
          return null;
        })
        .filter(Boolean)
        .join(', ');

      // 🔹 Non modificare la nota originale nel TextField
      // Mantieni tutto ciò che c'era, compresi i NO …
      setCurrentDeletedIds(Array.from(initialDeletedOptions));
      // setNote NON viene chiamato, così il TextField rimane intatto
      setShowNote(!!dish.note || !!initialDeletedNotesString);
    }

    // Apri il dialog
    setOpenVariantDialog(true);
  };



  const handleClickRemove = () => {
    setDialogText({
      text: `Vuoi eliminare "${dish.name}"?`,
      description: `L'azione sarà irreversibile.`
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = (accepted) => {
    if (accepted) {
      removeFromCart(dishIndex);
    }
    setOpenDialog(false);
  };

  const handleClickAddNote = () => {
    setShowNote(true);
  };

  const handleBlurNote = () => {
    if (note.length <= 150) {
      saveNote();
    }
  }

  const handleClickRemoveNote = () => {
    setShowNote(false);
    removeNote();
  };

  const handleNoteChange = (event) => {
    const newNote = event.target.value;
    if (newNote.length <= 150) {
      setNote(newNote);
      setError("");
    } else {
      setError("Note cannot exceed 150 characters.");
    }
  };

  const saveNote = () => {
    if (note.length <= 150) {
      setDishNote(dishIndex, note);
    }
  };

  const removeNote = () => {
    setDishNote(dishIndex, null);
  };


  const getSelectedVariants = () => {
    // Creiamo un oggetto per raggruppare le opzioni selezionate
    const groupedVariants = {};

    dish.variants
      .filter(variant => variant.selected && variant.selected.length > 0)
      .forEach(variant => {
        const { name, position, options, selected } = variant;

        // Inizializziamo l'array per il nome della variante se non esiste già
        if (!groupedVariants[name]) {
          groupedVariants[name] = {
            options: [],
            position: position // Memorizziamo la posizione
          };
        }
        const selectedOptions = options.filter(option => selected.some(sel => sel.id === option.id));
        const selectedOptionNames = selectedOptions.map(option => option.label);
        groupedVariants[name].options.push(...selectedOptionNames); // Aggiungiamo le opzioni selezionate all'array corrispondente

      });

    // Ordiniamo le varianti in base alla posizione
    const sortedVariants = Object.entries(groupedVariants).sort((a, b) => {
      return a[1].position - b[1].position; // Ordina per posizione
    })

    // Ora convertiamo l'oggetto in una stringa formattata
    return sortedVariants.map(([variantName, { options }]) => (
      <Typography key={variantName} variant="body2" sx={{ marginBottom: '4px' }}>
        <strong style={{ ...styleVariants }}>{variantName}:</strong>{' '}
        <span style={{ ...styleOptions }}>{options.join(', ')}</span>
      </Typography>
    ));
  };


  return (
    <>
      <Paper
        elevation={3}
        className={classes.paperItem}
        sx={{
          bgcolor: '#000 !important',
          color: 'white',
          borderRadius: 3,
        }}
      >
        <Grid container spacing={2} alignItems="center">

          {/* --- RIGA 1: NOME PIATTO & PREZZO --- */}
          <Grid item xs={7}>
            <Typography
              variant="h6"
              sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
            >
              {dish.name}
            </Typography>
          </Grid>

          <Grid item xs={5} container justifyContent="flex-end" alignItems="center">
            {isOwner ? (
              <>
                <IconButton
                  aria-label="decrease price"
                  onClick={() => updateDishPrice(dishIndex, -0.5)}
                  size="small"
                >
                  <RemoveIcon color="secondary" />
                </IconButton>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mx: 0.5 }}>
                  {(getDishPrice(dish) || 0).toFixed(2).replace('.', ',')} €
                </Typography>
                <IconButton
                  aria-label="increase price"
                  onClick={() => updateDishPrice(dishIndex, 0.5)}
                  size="small"
                >
                  <AddIcon color="primary" />
                </IconButton>
              </>
            ) : (
              <Typography variant="h5" color="primary" align="right" sx={{ fontWeight: 'bold' }}>
                {getDishPrice(dish) && getDishPrice(dish) !== 0
                  ? (amount * getDishPrice(dish)).toFixed(2).toString().replace('.', ',') + ' €'
                  : ""
                }
              </Typography>
            )}
          </Grid>

          {/* --- RIGA 2: VARIANTI (Dettagli) --- */}
          {dish.variants && dish.variants.length !== 0 && (
            <Grid item xs={12}>
              <Grid container spacing={1} alignItems="center">
                {/* Varianti selezionate */}
                <Grid item xs={10}>
                  <div className={classes.variantDetails}>
                    {getSelectedVariants()}
                  </div>
                </Grid>


                <Grid item xs={2}>
                  <Box
                    onClick={handleEditVariants}
                    role="button"
                    tabIndex={0}
                    aria-label="Modifica varianti del piatto"
                    sx={(theme) => ({
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      color: theme.palette.primary.main,
                      bgcolor: theme.palette.primary.main + '24',
                      borderRadius: 50,
                      px: 1.2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        bgcolor: theme.palette.primary.main + '66',
                        boxShadow: `0 0 5px ${theme.palette.primary.main}30`
                      }
                    })}
                  >
                    <EditIcon fontSize="small" />
                  </Box>
                </Grid>

              </Grid>
            </Grid>
          )}

          {/* --- RIGA 3: CONTROLLI QUANTITÀ & RIMOZIONE --- */}
          <Grid item xs={6} container alignItems="center">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                px: 0.5,
                width: 120,
                justifyContent: 'space-between'
              }}
            >
              {/* Bottone Rimuovi */}
              <IconButton
                size="small"
                onClick={() => handleChangeAmount(amount - 1)}
                disabled={amount < 2}
                color="secondary"
                aria-label="diminuisci quantità"
              >
                <RemoveIcon fontSize="small" />
              </IconButton>

              {/* Quantità Centrale */}
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {amount}
              </Typography>

              {/* Bottone Aggiungi */}
              <IconButton
                size="small"
                onClick={() => handleChangeAmount(amount + 1)}
                color="primary" // Usa il colore primario
                aria-label="aumenta quantità"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={6} container justifyContent="flex-end" alignItems="center">
            <Box
              onClick={handleClickRemove}
              role="button"
              tabIndex={0}
              sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: theme.palette.secondary.main,
                bgcolor: theme.palette.secondary.main + '24',
                borderRadius: 50,
                px: 1.2,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'background-color 0.2s, box-shadow 0.2s',
                '&:hover': {
                  bgcolor: theme.palette.secondary.main + '66', // '66' è circa il 40% di opacità
                  boxShadow: `0 0 5px ${theme.palette.secondary.main}30`
                }
              })}
              aria-label="Rimuovi piatto dal carrello"
            >
              <DeleteIcon fontSize="small" />
              <Typography
                component="span"
                variant="caption"
                sx={{ fontWeight: 'inherit', color: 'inherit' }}
              >
                Rimuovi
              </Typography>
            </Box>
          </Grid>

          {/* --- RIGA 4: NOTE (12 COLONNE) --- */}
          <Grid item xs={12} className={classes.noteSection}>
            {showNote ? (
              <Grid container alignItems="flex-start" spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    id="outlined-multiline-flexible"
                    placeholder={"Note per il piatto (es. Senza cipolla)"}
                    value={note}
                    fullWidth
                    variant="outlined"
                    onBlur={handleBlurNote}
                    onChange={handleNoteChange}
                    error={!!error}
                    helperText={error || `Caratteri: ${note.length}/150`}
                    size="small"
                    multiline
                    rows={2}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={handleClickRemoveNote}
                          size="small"
                          color="secondary"
                          aria-label="Rimuovi la nota e chiudi il campo"
                          sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            ) : (
              <Box
                onClick={handleClickAddNote}
                role="button"
                tabIndex={0}
                aria-label="Aggiungi una nota al piatto"
                sx={(theme) => ({
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: theme.palette.primary.main,
                  bgcolor: theme.palette.primary.main + '24',
                  borderRadius: 50,
                  px: 1.2,
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    bgcolor: theme.palette.primary.main + '66',
                    boxShadow: `0 0 5px ${theme.palette.primary.main}30`
                  }
                })}
              >
                <NoteAddIcon fontSize="small" /> {/* Icona ridimensionata a 16px */}

                <Typography
                  component="span"
                  variant="caption"
                  sx={{ fontWeight: 'inherit', color: 'inherit' }}
                >
                  Aggiungi nota
                </Typography>
              </Box>
            )}
          </Grid>

        </Grid>
      </Paper>
      <ConfirmDialog
        text={dialogText}
        open={openDialog}
        handleClose={handleCloseDialog}
        successTimeout={600}
      />



      {openVariantDialog && dishForDialog && (
        <>
          {isOwner ? (
            <Dialog fullScreen open={openVariantDialog} onClose={() => setOpenVariantDialog(false)}>
              <AppBar position="static">
                <Toolbar>
                  <Typography variant="h6" style={{ flexGrow: 1, }}>{dishForDialog.name}</Typography>
                  <IconButton edge="end" color="inherit" onClick={() => setOpenVariantDialog(false)}>
                    <CloseIcon />
                  </IconButton>
                </Toolbar>
              </AppBar>
              <MuiDialogContent
                sx={{
                  paddingBottom: '96px', // spazio riservato al bottone
                }}
              >
                <DishVariantsSelector
                  dish={{
                    ...dishForDialog,
                    variants: dishForDialog.variants.map(v => ({
                      ...v,
                      deletedOptions: v.deletedOptions || []
                    }))
                  }}
                  mode="owner"
                  variantsSelection={dishForDialog.variants
                    .filter(v => v.selected && v.selected.length > 0)
                    .map(v => ({ id: v.id, name: v.name, selected: v.selected }))}
                  setVariantsSelection={(updatedSelection) => {
                    const newVariants = dishForDialog.variants.map(originalVariant => {
                      const updated = updatedSelection.find(v => v.id === originalVariant.id);
                      return { ...originalVariant, selected: updated?.selected || [] };
                    });
                    setDishForDialog(prev => ({ ...prev, variants: newVariants }));
                  }}
                  onDeletedNotesChange={(notes, deletedIds) => {
                    setCurrentDeletedIds(deletedIds || []);
                  }}
                />
              </MuiDialogContent>

              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  backgroundColor: '#000',
                  px: 2,
                  py: 1.5,
                  boxShadow: '0 -4px 10px rgba(0,0,0,0.35)',
                  zIndex: 1300,
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{
                    fontSize: 18,
                    py: 1.5,
                    borderRadius: 2,
                  }}
                  onClick={() => {
                    // 🔹 Ricrea la stringa NO … dalle opzioni eliminate correnti
                    const deletedNotesString = currentDeletedIds
                      .map(id => {
                        for (const variant of dishForDialog.variants) {
                          const opt = variant.options.find(o => o.id === id);
                          if (opt) return `NO ${opt.label}`;
                        }
                        return null;
                      })
                      .filter(Boolean);

                    // 🔹 Gestione corretta dei duplicati e dei NO rimossi
                    const noteParts = note.split(',').map(n => n.trim()).filter(Boolean);

                    // Mantieni solo la nota utente + i NO … ancora selezionati
                    const userNoteParts = noteParts.filter(
                      part => !part.toUpperCase().startsWith('NO ')
                    );

                    // Combina nota utente + NO correnti
                    const newNote = [...userNoteParts, ...deletedNotesString]
                      .join(', ')
                      .trim();

                    // Salva la nota completa
                    setDishNote(dishIndex, newNote);

                    // Aggiorna le varianti con le opzioni eliminate
                    const variantsToSave = dishForDialog.variants
                      .map(v => {
                        const newDeletedOptions = currentDeletedIds.filter(id =>
                          v.options.some(opt => opt.id === id)
                        );
                        return { ...v, deletedOptions: newDeletedOptions };
                      })
                      .filter(v =>
                        (v.selected && v.selected.length > 0) ||
                        (v.deletedOptions && v.deletedOptions.length > 0)
                      );

                    updateDishVariants(dishIndex, variantsToSave);

                    setOpenVariantDialog(false);
                  }}
                >
                  Conferma
                </Button>
              </Box>
            </Dialog>
          ) : (
            <UserReservationDialog
              dish={dishForDialog}
              open={openVariantDialog}
              onClose={() => setOpenVariantDialog(false)}
              onConfirm={(updatedDish) => {
                changeAmount(dishIndex, updatedDish.amount);
                setDishNote(dishIndex, updatedDish.note);
                updateDishVariants(dishIndex, updatedDish.variants);
                setOpenVariantDialog(false);
              }}
              mode="edit"
              initialAmount={amount}
              initialVariants={dish.variants}
              initialNote={note}
            />
          )}
        </>
      )}
    </>
  );
};

export default UserCartDish;